import { jwtDecode } from 'jwt-decode';
import HttpClient from './http_client';
import { isExtension } from '../utils/environment';
import { ExtensionStorage } from '../utils/ExtensionStorage';

let anonymousUserPromise = null;

// Groups cache with TTL (30 seconds)
const groupsCache = {
  data: null,
  timestamp: 0,
  TTL: 30000,
};

const DEFAULT_PER_PAGE = 24;
const TOKEN_STORAGE_KEY = 'jwt_token';
const USER_ID_KEY = 'user_id';
const STATUS_KEY = 'status';
const CONFIG_KEY = 'user_config';

const extractBookmarksFromContainer = result => {
  if (result && !Array.isArray(result) && Array.isArray(result.bookmarks)) {
    const bookmarks = result.bookmarks;
    if (result._meta) bookmarks._meta = result._meta;
    if (result._pagination) bookmarks._pagination = result._pagination;
    return bookmarks;
  }

  return result;
};

export default class Client {
  constructor() {
    // Initialize the HTTP client with a reference to this client instance
    this.httpClient = new HttpClient(null, this);
    this._userEnsured = false;
  }

  // Storage methods that work in both web and extension contexts
  async getToken() {
    if (isExtension()) {
      return await ExtensionStorage.getToken();
    }
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  }

  async setToken(token) {
    if (isExtension()) {
      return await ExtensionStorage.setToken(token);
    }

    if (token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  }

  async setUserId(userId) {
    if (isExtension()) {
      return await ExtensionStorage.setUserId(userId);
    }

    if (userId) {
      localStorage.setItem(USER_ID_KEY, userId);
    } else {
      localStorage.removeItem(USER_ID_KEY);
    }
  }

  async getUserId() {
    if (isExtension()) {
      return await ExtensionStorage.getUserId();
    }
    return localStorage.getItem(USER_ID_KEY);
  }

  async setStatus(status) {
    if (isExtension()) {
      return await ExtensionStorage.setStatus(status);
    }

    if (status) {
      localStorage.setItem(STATUS_KEY, status);
    } else {
      localStorage.removeItem(STATUS_KEY);
    }
  }

  async getStatus() {
    // Try to get status from local storage first
    const cachedStatus = isExtension()
      ? await ExtensionStorage.getStatus()
      : localStorage.getItem(STATUS_KEY);

    // Return cached status if available
    if (cachedStatus) {
      return cachedStatus;
    }

    // Only fetch from API if not cached
    try {
      const token = await this.getToken();
      if (token) {
        const response = await this.httpClient.get('/me', token);
        if (response && response.status) {
          // Update local storage with the latest status
          await this.setStatus(response.status);
          return response.status;
        }
      }
    } catch (error) {
      // If API call fails, return null
      console.warn('Failed to get status from API:', error);
    }

    return null;
  }

  async existsUser() {
    const token = await this.getToken();
    return token != null;
  }

  async createAnonymousUser() {
    if (anonymousUserPromise) {
      return anonymousUserPromise;
    }

    anonymousUserPromise = (async () => {
      try {
        if (await this.existsUser()) {
          return;
        }

        // Clear last used group before creating new anonymous user
        if (isExtension()) {
          try {
            await ExtensionStorage.setLastUsedGroupId(null);
          } catch (error) {
            console.warn(
              'Failed to clear last used group ID for new anonymous user:',
              error
            );
          }
        }

        const response = await this.httpClient.postWithFullResponse(
          '/users/anonymous',
          null,
          {}
        );

        await this.processAuthHeader(response.headers);

        return response.data;
      } catch (error) {
        anonymousUserPromise = null;
        throw error;
      }
    })();

    return anonymousUserPromise;
  }

  async ensureUser() {
    if (this._userEnsured) return;

    if (await this.existsUser()) {
      this._userEnsured = true;
      return;
    }

    await this.createAnonymousUser();
    this._userEnsured = true;
  }

  async register(email, password, passwordConfirmation, termsAccepted) {
    // Clear last used group before registration to prevent cross-user contamination
    if (isExtension()) {
      try {
        await ExtensionStorage.setLastUsedGroupId(null);
      } catch (error) {
        console.warn(
          'Failed to clear last used group ID during registration:',
          error
        );
      }
    }

    const token = await this.getToken();
    const response = await this.httpClient.postWithFullResponse(
      '/users/sign_up',
      token,
      {
        user: {
          email,
          password,
          password_confirmation: passwordConfirmation,
          terms_accepted: termsAccepted,
        },
      }
    );

    await this.processAuthHeader(response.headers);

    // Handle both registration response types:
    // - Anonymous user conversion returns user data
    // - New user registration returns validation message
    if (response.data && !response.message) {
      // Anonymous user conversion - return user data (no message field)
      return response.data;
    } else if (response.message && !response.data) {
      // New user registration with email validation message
      return { message: response.message };
    }

    // Fallback - return whatever we got
    return response.data || response;
  }

  async login(username, password) {
    // Clear last used group before login to prevent cross-user contamination
    if (isExtension()) {
      try {
        await ExtensionStorage.setLastUsedGroupId(null);
      } catch (error) {
        console.warn('Failed to clear last used group ID during login:', error);
      }
    }

    const response = await this.httpClient.postWithFullResponse(
      '/users/sign_in',
      null,
      { user: { email: username, password } }
    );

    await this.processAuthHeader(response.headers);

    return response.data;
  }

  async loggedIn() {
    const token = await this.getToken();
    if (!token) return false;

    // Use status field to determine logged in state
    const status = await this.getStatus();
    if (status) {
      // Users with pending_confirmation or user status are considered logged in
      return status === 'user' || status === 'pending_confirmation';
    }

    // If no status is set, the user is not logged in
    return false;
  }

  async logout() {
    const token = await this.getToken();

    // Try to call the logout endpoint, but don't fail if it doesn't work
    try {
      if (token) {
        await this.httpClient.delete('/users/sign_out', token);
      }
    } catch {
      // Intentionally ignore logout errors
    }

    // Always clear local storage/session data regardless of API response
    await this.setToken(null);
    await this.setUserId(null);
    await this.setStatus(null);

    // Clear user configuration as well
    this.setConfiguration(null);

    // Reset user ensured flag
    this._userEnsured = false;

    // Clear anonymous user promise to allow fresh user creation
    anonymousUserPromise = null;

    // Clear all extension storage to prevent cross-user contamination
    if (isExtension()) {
      try {
        await ExtensionStorage.clearAll();
      } catch {
        // Intentionally ignore storage errors during logout
      }
    }

    return true;
  }

  async deleteAccount() {
    const token = await this.getToken();

    if (!token) {
      throw new Error('No user token found');
    }

    // Call the delete account endpoint
    await this.httpClient.delete('/me', token);

    // Clear all local data
    await this.setToken(null);
    await this.setUserId(null);
    await this.setStatus(null);
    this.setConfiguration(null);

    // Reset user ensured flag
    this._userEnsured = false;

    // Clear anonymous user promise to allow fresh user creation
    anonymousUserPromise = null;

    // Clear all extension storage to prevent cross-user contamination
    if (isExtension()) {
      try {
        await ExtensionStorage.clearAll();
      } catch {
        // Intentionally ignore storage errors during account deletion
      }
    }

    // Create a new anonymous user
    await this.createAnonymousUser();

    return true;
  }

  async validateEmail(token) {
    const response = await this.httpClient.getWithFullResponse(
      `/email_validation?token=${encodeURIComponent(token)}`,
      null
    );

    return response.data;
  }

  async resendEmailValidation(email = null) {
    const token = await this.getToken();
    const payload = email ? { email } : {};

    const response = await this.httpClient.postWithFullResponse(
      '/email_validation/resend',
      token,
      payload
    );

    return response.data;
  }

  async forgotPassword(email) {
    const response = await this.httpClient.postWithFullResponse(
      '/users/forgot_password',
      null,
      { email }
    );

    return response.data;
  }

  async fetchMe() {
    const token = await this.getToken();

    if (!token) {
      throw new Error('No user token found');
    }

    const response = await this.httpClient.get('/me', token);
    return response;
  }

  async updatePassword(currentPassword, password, passwordConfirmation) {
    const token = await this.getToken();

    if (!token) {
      throw new Error('No user token found');
    }

    const response = await this.httpClient.put('/me/password', token, {
      current_password: currentPassword,
      password,
      password_confirmation: passwordConfirmation,
    });

    return response;
  }

  async syncAuthStatus() {
    try {
      // Make an API call that will trigger processAuthHeader if the user's status has changed
      const token = await this.getToken();
      if (!token) return false;

      const response = await this.httpClient.getWithFullResponse(
        '/me/groups',
        token
      );

      // processAuthHeader will be called automatically if the server returns a new token
      await this.processAuthHeader(response.headers);

      return true;
    } catch (error) {
      console.error('Failed to refresh auth status:', error);
      return false;
    }
  }

  async fetchGroups() {
    await this.ensureUser();

    const token = await this.getToken();
    const result = await this.httpClient.get('/me/groups', token);

    return result;
  }

  async fetchGroup(group_id) {
    await this.ensureUser();
    const token = await this.getToken();
    return await this.httpClient.get(`/me/groups/${group_id}`, token);
  }

  async fetchDefaultGroup() {
    await this.ensureUser();
    const token = await this.getToken();
    return await this.httpClient.get('/me/groups/default', token);
  }

  async fetchReadLaterGroup() {
    await this.ensureUser();
    const token = await this.getToken();
    return await this.httpClient.get('/me/groups/read_later', token);
  }

  async fetchBookmarks(
    group_id,
    pageNumber,
    orderField = null,
    order = null,
    perPage = null
  ) {
    await this.ensureUser();

    const queryParams = {
      page: pageNumber,
      per_page: perPage || DEFAULT_PER_PAGE,
    };

    // Only include order parameters if they are provided
    if (orderField) queryParams.order_field = orderField;
    if (order) queryParams.order = order;

    const token = await this.getToken();
    const result = await this.httpClient.get(
      `/me/groups/${group_id}/bookmarks`,
      token,
      queryParams
    );

    return extractBookmarksFromContainer(result);
  }

  async fetchGroupWithBookmarks(
    group_id,
    pageNumber = 1,
    orderField = null,
    order = null,
    perPage = null
  ) {
    await this.ensureUser();

    const queryParams = {
      page: pageNumber,
      per_page: perPage || DEFAULT_PER_PAGE,
    };

    // Only include order parameters if they are provided
    if (orderField) queryParams.order_field = orderField;
    if (order) queryParams.order = order;

    const token = await this.getToken();
    // This endpoint uses v2 of the API which combines group and bookmarks in a single response
    const result = await this.httpClient.get(
      `/v2/me/groups/${group_id}/bookmarks`,
      token,
      queryParams
    );

    return result;
  }

  async fetchSmartCollectionWithBookmarks(
    smartCollectionId,
    pageNumber = 1,
    perPage = null
  ) {
    await this.ensureUser();

    const queryParams = {
      page: pageNumber,
      per_page: perPage || DEFAULT_PER_PAGE,
    };

    const token = await this.getToken();

    return await this.httpClient.get(
      `/me/bookmarks/smart_collections/${smartCollectionId}`,
      token,
      queryParams
    );
  }

  async fetchSmartCollectionBookmarks(
    smartCollectionId,
    pageNumber = 1,
    perPage = null
  ) {
    const result = await this.fetchSmartCollectionWithBookmarks(
      smartCollectionId,
      pageNumber,
      perPage
    );

    return extractBookmarksFromContainer(result);
  }

  async fetchSearch(query, pageNumber = 1, perPage = null) {
    await this.ensureUser();
    const token = await this.getToken();

    return await this.httpClient.get('/me/bookmarks/search', token, {
      query,
      page: pageNumber,
      per_page: perPage || DEFAULT_PER_PAGE,
    });
  }

  async fetchAllBookmarks() {
    await this.ensureUser();
    const token = await this.getToken();

    return await this.httpClient.get('/v2/me/bookmarks', token);
  }

  async fetchTags() {
    await this.ensureUser();
    const token = await this.getToken();

    const result = await this.httpClient.get('/v2/me/tags', token);
    return Array.isArray(result) ? result : [];
  }

  async reportBookmarkContentUpdateDetector(bookmarkId, payload) {
    const token = await this.getToken();
    if (!token) {
      const error = new Error('No user token found');
      error.status = 401;
      throw error;
    }

    return await this.httpClient.put(
      `/me/bookmarks/${bookmarkId}/content_update_detector`,
      token,
      payload
    );
  }

  async visit(bookmark_id) {
    await this.ensureUser();
    const token = await this.getToken();

    return await this.httpClient.post('/me/visits', token, { bookmark_id });
  }

  async visitByUrl(url) {
    await this.ensureUser();
    const token = await this.getToken();

    return await this.httpClient.post('/me/visits', token, { url });
  }

  async updateBookmarkPosition(group_id, bookmark_id, position) {
    await this.ensureUser();
    const token = await this.getToken();

    return await this.httpClient.patch(
      `/me/groups/${group_id}/bookmarks/${bookmark_id}/update_position`,
      token,
      { position }
    );
  }

  async updateGroupPosition(group_id, position) {
    await this.ensureUser();
    const token = await this.getToken();

    return await this.httpClient.patch(
      `/me/groups/${group_id}/update_position`,
      token,
      { position }
    );
  }

  async deleteBookmark(group_id, bookmark_id) {
    await this.ensureUser();
    const token = await this.getToken();

    return await this.httpClient.delete(
      `/me/groups/${group_id}/bookmarks/${bookmark_id}`,
      token
    );
  }

  async undeleteBookmark(group_id, bookmark_id) {
    await this.ensureUser();
    const token = await this.getToken();

    return await this.httpClient.patch(
      `/me/groups/${group_id}/bookmarks/${bookmark_id}/undelete`,
      token
    );
  }

  async updateBookmark(group_id, bookmark_id, title, url) {
    await this.ensureUser();
    const token = await this.getToken();

    return await this.httpClient.put(
      `/me/groups/${group_id}/bookmarks/${bookmark_id}`,
      token,
      { title, url }
    );
  }

  async moveBookmark(current_group_id, bookmark_id, target_group_id) {
    await this.ensureUser();
    const token = await this.getToken();

    return await this.httpClient.patch(
      `/me/groups/${current_group_id}/bookmarks/${bookmark_id}/move`,
      token,
      { target_group_id }
    );
  }

  async getBookmark(group_id, bookmark_id) {
    await this.ensureUser();
    const token = await this.getToken();

    return await this.httpClient.get(
      `/me/groups/${group_id}/bookmarks/${bookmark_id}`,
      token
    );
  }

  async updateBookmarkWithFiles(groupId, bookmarkId, formData) {
    return await this.sendMultipartRequest(
      `/me/groups/${groupId}/bookmarks/${bookmarkId}`,
      'PUT',
      formData,
      'Failed to update bookmark'
    );
  }

  async createGroup(title, gradientKey = 'gradient1') {
    await this.ensureUser();
    const token = await this.getToken();

    const result = await this.httpClient.post('/me/groups', token, {
      title,
      gradient_key: gradientKey,
    });

    // Invalidate groups cache
    Client.clearGroupsCache();
    return result;
  }

  async updateGroup(
    group_id,
    title,
    gradientKey,
    sortOrder = null,
    sortDirection = null
  ) {
    await this.ensureUser();
    const token = await this.getToken();

    const params = { title, gradient_key: gradientKey };
    if (sortOrder) params.sort_order = sortOrder;
    if (sortDirection) params.sort_direction = sortDirection;

    const result = await this.httpClient.put(
      `/me/groups/${group_id}`,
      token,
      params
    );

    // Invalidate groups cache
    Client.clearGroupsCache();
    return result;
  }

  async deleteGroup(group_id) {
    await this.ensureUser();
    const token = await this.getToken();
    const result = await this.httpClient.delete(
      `/me/groups/${group_id}`,
      token
    );

    // Invalidate groups cache
    Client.clearGroupsCache();
    return result;
  }

  async fetchConfiguration() {
    if (!(await this.loggedIn())) {
      return null;
    }

    try {
      const token = await this.getToken();
      const config = await this.httpClient.get('/me/configuration', token);
      this.setConfiguration(config);
      return config;
    } catch (error) {
      // Only log unexpected errors, not rate limiting or auth issues
      if (
        error.status !== 429 &&
        error.status !== 401 &&
        error.status !== 404
      ) {
        console.error('Error fetching user configuration:', error);
      }
      return null;
    }
  }

  async updateConfiguration(configData) {
    if (!(await this.loggedIn())) {
      return null;
    }

    try {
      const token = await this.getToken();
      const config = await this.httpClient.put(
        '/me/configuration',
        token,
        configData
      );
      this.setConfiguration(config);
      return config;
    } catch (error) {
      // Only log unexpected errors, not rate limiting or auth issues
      if (
        error.status !== 429 &&
        error.status !== 401 &&
        error.status !== 404
      ) {
        console.error('Error updating user configuration:', error);
      }
      throw error;
    }
  }

  setConfiguration(config) {
    if (isExtension()) {
      // For extension, we should also use extension storage
      ExtensionStorage.setConfiguration(config).catch(console.error);
    } else {
      if (config) {
        localStorage.setItem(CONFIG_KEY, JSON.stringify(config));
      } else {
        localStorage.removeItem(CONFIG_KEY);
      }
    }
  }

  async getConfiguration() {
    if (isExtension()) {
      return await ExtensionStorage.getConfiguration();
    } else {
      const config = localStorage.getItem(CONFIG_KEY);
      return config ? JSON.parse(config) : null;
    }
  }

  // Extension-specific methods with caching
  async getGroups(forceRefresh = false) {
    await this.ensureUser();

    // Return cached data if still valid
    const now = Date.now();
    if (
      !forceRefresh &&
      groupsCache.data &&
      now - groupsCache.timestamp < groupsCache.TTL
    ) {
      return groupsCache.data;
    }

    const token = await this.getToken();
    const result = await this.httpClient.get('/me/groups', token);

    // Update cache
    groupsCache.data = result;
    groupsCache.timestamp = now;

    return result;
  }

  // Clear groups cache (call after group mutations)
  static clearGroupsCache() {
    groupsCache.data = null;
    groupsCache.timestamp = 0;
  }

  async saveBookmarkWithFiles(groupId, formData) {
    return await this.sendMultipartRequest(
      `/me/groups/${groupId}/bookmarks`,
      'POST',
      formData,
      'Failed to save bookmark'
    );
  }

  async importChromeBookmarks(file) {
    if (!file) {
      throw new Error('Chrome bookmarks file is required');
    }

    const formData = new FormData();
    formData.append('file', file);

    const result = await this.sendMultipartRequest(
      '/me/bookmarks/import_chrome',
      'POST',
      formData,
      'Failed to import Chrome bookmarks'
    );

    Client.clearGroupsCache();
    return result;
  }

  async importFirefoxBookmarks(file) {
    if (!file) {
      throw new Error('Firefox bookmarks file is required');
    }

    const formData = new FormData();
    formData.append('file', file);

    const result = await this.sendMultipartRequest(
      '/me/bookmarks/import_firefox',
      'POST',
      formData,
      'Failed to import Firefox bookmarks'
    );

    Client.clearGroupsCache();
    return result;
  }

  async exportChromeBookmarks() {
    await this.ensureUser();

    const token = await this.getToken();
    const response = await this.httpClient.getBlobWithFullResponse(
      '/me/bookmarks/export_chrome',
      token
    );

    if (response.headers.get('Authorization')) {
      await this.processAuthHeader(response.headers);
    }

    return {
      blob: response.blob,
      filename: this.extractDownloadFilename(
        response.headers.get('Content-Disposition')
      ),
      contentType: response.contentType,
    };
  }

  async exportFirefoxBookmarks() {
    await this.ensureUser();

    const token = await this.getToken();
    const response = await this.httpClient.getBlobWithFullResponse(
      '/me/bookmarks/export_firefox',
      token
    );

    if (response.headers.get('Authorization')) {
      await this.processAuthHeader(response.headers);
    }

    return {
      blob: response.blob,
      filename: this.extractDownloadFilename(
        response.headers.get('Content-Disposition')
      ),
      contentType: response.contentType,
    };
  }

  async processAuthHeader(headers) {
    const authHeader = headers.get('Authorization');
    if (authHeader && authHeader.startsWith('Bearer ')) {
      const token = authHeader.substring(7);
      const decodedToken = jwtDecode(token);

      // Get current user ID to check if it's changing
      const currentUserId = await this.getUserId();

      await this.setToken(token);
      await this.setUserId(decodedToken.user_id);

      // Store status from the token
      if (decodedToken.status) {
        await this.setStatus(decodedToken.status);
      }

      // Clear last used group if switching to a different user
      if (
        isExtension() &&
        currentUserId &&
        currentUserId !== decodedToken.user_id.toString()
      ) {
        try {
          await ExtensionStorage.setLastUsedGroupId(null);
        } catch (error) {
          console.warn(
            'Failed to clear last used group ID on user change:',
            error
          );
        }
      }

      // Fetch user configuration after successful authentication
      if (decodedToken.status === 'user') {
        await this.fetchConfiguration();
      }

      return decodedToken;
    }

    return null;
  }

  async sendMultipartRequest(path, method, formData, errorMessage) {
    await this.ensureUser();

    const token = await this.getToken();
    const headers = {
      Accept: 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const versionedPath = this.httpClient.getVersionedPath(path);
    const response = await fetch(`${this.httpClient.apiHost}${versionedPath}`, {
      method,
      headers,
      body: formData,
    });
    const responseText = await response.text();

    if (!response.ok) {
      throw this.buildMultipartRequestError(
        response.status,
        responseText,
        errorMessage
      );
    }

    if (response.headers.get('Authorization')) {
      await this.processAuthHeader(response.headers);
    }

    return responseText ? JSON.parse(responseText) : null;
  }

  buildMultipartRequestError(status, responseText, errorMessage) {
    try {
      const errorData = responseText ? JSON.parse(responseText) : {};
      const extractedError = this.httpClient.extractErrorData(errorData);
      const structuredError = new Error(`${errorMessage}: ${status}`);
      structuredError.errors = extractedError.errors;
      structuredError.meta = extractedError.meta;
      structuredError.status = status;
      return structuredError;
    } catch {
      const fallbackError = new Error(
        `${errorMessage}: ${status}${responseText ? ` - ${responseText}` : ''}`
      );
      fallbackError.status = status;
      fallbackError.errors = [{ message: `HTTP ${status}` }];
      fallbackError.meta = {};
      return fallbackError;
    }
  }

  extractDownloadFilename(contentDisposition) {
    const filenameMatch = contentDisposition
      ?.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i)
      ?.at(1);

    return filenameMatch
      ? decodeURIComponent(filenameMatch.replace(/"$/, ''))
      : 'exlibris-chrome-bookmarks.html';
  }
}
